import { useCallback, useEffect, useState } from 'react';
import { decodeJwt } from 'jose';
const ONE_MINUTE_IN_MILLISECONDS = 60 * 1000;
export default function useConnectionDetails(appConfig) {
    // Generate room connection details, including:
    //   - A random Room name
    //   - A random Participant name
    //   - An Access Token to permit the participant to join the room
    //   - The URL of the server to connect to
    const [connectionDetails, setConnectionDetails] = useState(null);
    const fetchConnectionDetails = useCallback(async () => {
        var _a, _b, _c;
        setConnectionDetails(null);
        // Use connectionDetailsEndpoint from appConfig if available (for embedded usage)
        // Otherwise fall back to env var or relative path
        const endpoint = (_b = (_a = appConfig.connectionDetailsEndpoint) !== null && _a !== void 0 ? _a : process.env.NEXT_PUBLIC_CONN_DETAILS_ENDPOINT) !== null && _b !== void 0 ? _b : '/api/connection-details';
        const url = new URL(endpoint, window.location.origin);
        let data;
        try {
            const res = await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-Sandbox-Id': (_c = appConfig.sandboxId) !== null && _c !== void 0 ? _c : '',
                },
                body: JSON.stringify({
                    agentId: appConfig.agentId,
                    room_config: appConfig.agentName
                        ? {
                            agents: [{ agent_name: appConfig.agentName }],
                        }
                        : undefined,
                }),
            });
            data = await res.json();
        }
        catch (error) {
            console.error('Error fetching connection details:', error);
            throw new Error('Error fetching connection details!');
        }
        setConnectionDetails(data);
        return data;
    }, [
        appConfig.agentId,
        appConfig.agentName,
        appConfig.sandboxId,
        appConfig.connectionDetailsEndpoint,
    ]);
    useEffect(() => {
        fetchConnectionDetails();
    }, [fetchConnectionDetails]);
    const isConnectionDetailsExpired = useCallback(() => {
        const token = connectionDetails === null || connectionDetails === void 0 ? void 0 : connectionDetails.participantToken;
        if (!token) {
            return true;
        }
        const jwtPayload = decodeJwt(token);
        if (!jwtPayload.exp) {
            return true;
        }
        const expiresAt = new Date(jwtPayload.exp - ONE_MINUTE_IN_MILLISECONDS);
        const now = new Date();
        return expiresAt >= now;
    }, [connectionDetails === null || connectionDetails === void 0 ? void 0 : connectionDetails.participantToken]);
    const existingOrRefreshConnectionDetails = useCallback(async () => {
        if (isConnectionDetailsExpired() || !connectionDetails) {
            return fetchConnectionDetails();
        }
        else {
            return connectionDetails;
        }
    }, [connectionDetails, fetchConnectionDetails, isConnectionDetailsExpired]);
    return {
        connectionDetails,
        refreshConnectionDetails: fetchConnectionDetails,
        existingOrRefreshConnectionDetails,
    };
}
